// i18n area 'realms' — the DirectoryRealms console page (GUI-PARITÀ ondata
// 1a, #181 2026-08-19; contract directoryrealms.yaml, NAS-2 Fase 1a). Own
// root on purpose: the dictionary spread is shallow, a second section with
// the `users` root would clobber sections/system.ts.

export const en = {
  realms: {
    title: 'Directory realms',
    createTitle: 'Declare a directory realm',
    editTitle: 'Edit realm {name}',
    edit: 'Edit',
    createHint:
      'Declaring a realm is a DECLARATION: nothing changes on any node — the join happens when a file share binds to the realm. Run the preflight afterwards: it checks the prerequisites one by one, with a remedy for each. Every mutation here is sudo-protected.',
    editHint:
      'Writable: credential, servers, the per-type fields, the identity key (only while no uid is engraved), the offline window and the notes. Name, type, uid range and domain SID are immutable — the range keys files already on disk; rebinding the SID has its own gesture (refound).',
    name: 'Name',
    nameHint:
      'Lower-case DNS label, unique cluster-wide, IMMUTABLE — it is the qualifying half of every REALM\\user login and the key of the uid range. `local` and `files` are reserved.',
    type: 'Type',
    types: {
      ad: 'Active Directory',
      ldap: 'LDAP'
    },
    typeHints: {
      ad: 'Kerberos/NTLMSSP against a domain controller, winbind as resolver — the only branch SMB can use. Immutable.',
      ldap: 'A plain LDAP directory: bind over mandatory TLS, SSSD as resolver — serves SFTP/FTP and web sessions, never SMB. Immutable.'
    },
    credential: 'Directory account',
    credentialHint:
      'The keystore entry of kind directoryAccount the appliance authenticates AS: the JOIN account on AD, the BIND account on LDAP. The secret lives in the keystore — the realm carries the reference.',
    credentialRepointHint: 'Repointing the reference is how the join/bind account is replaced — never a new realm.',
    noCredentials:
      'No directoryAccount credential exists yet — create one under System → Credentials first: a realm carries the reference, the secret lives in the keystore.',
    servers: 'Directory servers',
    serversHintAd:
      'Optional on Active Directory: an EMPTY list means discovery via SRV DNS records, which is the normal road.',
    serversHintLdap:
      'Required on LDAP (at least one): plain directories almost never publish SRV records, so an empty list is a configuration that cannot work — refused when written, not at the first login.',
    serverPlaceholder: 'host or host:port',
    addServer: 'Add server',
    kerberosRealm: 'Kerberos realm',
    kerberosRealmHint:
      'UPPER CASE (e.g. CORP.EXAMPLE) — Kerberos realm names are case sensitive; a lower-case one fails at the KDC with a message that never names the typo.',
    netbiosDomain: 'NetBIOS domain',
    netbiosDomainHint:
      'The short domain name, UPPER CASE, max 15 chars — what Windows users type before the backslash.',
    machineAccountOu: 'Machine account OU',
    machineAccountOuHint:
      'Distinguished name of the OU the machine account is born in. Empty = the domain’s own default computer container.',
    machineAccountOuDefault: 'Domain default container',
    baseDn: 'Base DN',
    baseDnHint: 'The subtree the appliance searches (e.g. dc=example,dc=com). There is no default.',
    tls: 'TLS',
    tlsHint:
      'Mandatory on this branch — the user’s own password travels to the directory, so cleartext cannot even be spelled.',
    tlsOptions: {
      ldaps: 'LDAPS — TLS from the first byte (636)',
      startTls: 'StartTLS — negotiated on the plain port (389)'
    },
    userFilter: 'User filter',
    userFilterHint: 'Optional LDAP filter narrowing which entries count as users. Empty = the resolver’s default.',
    groupFilter: 'Group filter',
    groupFilterHint: 'The same, for groups. Empty = the resolver’s default.',

    // ENEA-A-R6 (2026-10-23): the Kerberos half of an ldap realm and the group→role map.
    kerberos: {
      title: 'Kerberos (MIT)',
      absent: 'None — SSSD authenticates with the LDAP bind',
      enable: 'Kerberos MIT: authenticate against a KDC and serve NFS krb5',
      enableHint:
        'With it, SSSD authenticates the directory’s users against the KDCs below instead of the LDAP bind, and the realm’s NFS shares may ask sec=krb5/krb5i/krb5p once the nfs/ keytab is attached. The web login stays the LDAP bind.',
      realm: 'Kerberos realm',
      realmHint: 'UPPER CASE (e.g. TVSTOR165.TEST) — MIT treats the case as significant.',
      kdcs: 'KDCs',
      kdcsHint: 'One to eight, host or host:port, separated by commas — tried in order; no DNS discovery.',
      nfsHostname: 'NFS host name',
      nfsHostnameHint:
        'The host part of the nfs/ principal: the SHARED name clients mount the gateways by (one name, N gateways), lower case.',
      adminServer: 'Admin server',
      adminServerHint: 'Optional, informational (the appliance never runs kadmin).',
      keytabCredential: 'nfs/ keytab credential',
      keytabCredentialHint:
        'A machineAccount credential of the KEYTAB form holding nfs/<host>@<REALM>: created on the KDC with kadmin (addprinc -randkey, ktadd) and uploaded with the CLI (create-credential --kind machineAccount --keytab-file …). None = no NFS Kerberos yet.',
      keytabNone: 'none yet'
    },
    adminRoles: {
      title: 'Administrative login (group → role)',
      hint: 'Members of a mapped group log in to the console with the role (tvstor-admin, tvstor-operator, tvstor-viewer or a custom role) on the scope (empty = the realm’s scope). Users in no mapped group stay kiosk users. The group is a DN on ldap, @DOM\\name on ad. Evaluated at every request from the session’s membership, refreshed every 15 minutes.',
      group: 'Group',
      role: 'Role',
      scope: 'Scope (optional)',
      add: 'Add a mapping',
      none: 'No administrative login from this realm'
    },
    declareUidRange: 'Declare the uid range',
    uidRangeHint:
      'Unchecked = the platform takes the first free slot of the grid (2 000 000 + k·1 000 000). Checked = your own base and span are engraved. IMMUTABLE either way: moving an engraved range would change the owner of files already written. An overlap is refused naming the occupying realm.',
    uidBase: 'Base uid',
    uidBaseHint:
      'Floor 2 000 000 (below live the system accounts); ceiling 1 000 000 000 (above starts the SID-derived resolver window).',
    uidSpan: 'Span',
    uidSpanHint: 'How many uids the realm may engrave, starting at the base.',
    domainSid: 'Domain SID',
    domainSidHint:
      'Optional, AD only (S-1-5-21-…): declare it if you know it and the name-vs-domain check happens NOW instead of at a failing join. Omitted = the first join records it.',
    domainSidNone: 'No domain has claimed this name yet',
    identityKeyAttribute: 'Identity key attribute',
    identityKeyAttributeHint:
      'Which directory attribute is a user’s STABLE key. Empty = the per-type default (AD → objectGUID, LDAP → entryUUID). Attributes known to be unstable are refused with the reason.',
    identityKeyLocked: 'Locked: this realm has already engraved uids — re-keying would re-key the people behind them.',
    offlineWindowSeconds: 'Offline window (seconds)',
    offlineWindowHint:
      'How long an already-authenticated user keeps working while the directory does not answer. 0 = every request revalidates (no directory, no files session). Empty = the default compromise, one hour.',
    offlineWindowZero: '0 — every request revalidates',
    uidRange: 'UID range',
    uidRangeDeclared: 'Declared',
    uidRangeAuto: 'Automatic',
    allocatedIdentities: 'Allocated identities',
    srvDiscovery: 'SRV DNS discovery',
    deleteTitle: 'Delete realm {name}',
    deleteBody:
      'The declaration goes; the uid range and its allocations STAY as a tombstone (an allocation is never released — the files those uids own are still on disk; recreating the name resumes both). The realm’s live web sessions are revoked in the same transaction. A realm a file share depends on refuses, naming the shares.',
    preflight: 'Preflight',
    preflightTitle: 'Preflight — {name}',
    preflightHint:
      'What can be checked BEFORE touching the domain, one prerequisite at a time. Three-valued on purpose: “unknown” never means fine and never means missing — every fail and every unknown carries its remedy. On AD, two prerequisites are structurally unobservable before the join (clock skew, OU rights), so “ready” cannot be reached in this phase.',
    preflightRunning: 'Asking the answering node…',
    preflightAgain: 'Run again',
    observedFrom:
      'Observed from {node} — domain adhesion is per node: a good outcome means visible from THIS node, not from all of them.',
    outcomes: {
      ready: 'Ready',
      blocked: 'Blocked',
      inconclusive: 'Inconclusive'
    },
    verdicts: {
      pass: 'Pass',
      fail: 'Fail',
      unknown: 'Unknown'
    },
    checks: {
      declaration: 'Declaration shape',
      credential: 'Directory account credential',
      idRange: 'UID range engraved',
      idRangeDisjoint: 'UID range disjoint',
      domainIdentity: 'Domain identity (SID)',
      identityKey: 'Identity key attribute',
      identityKeyInDirectory: 'Identity key in the directory',
      dnsSrv: 'SRV DNS records',
      serverDns: 'Server DNS (forward)',
      reverseDns: 'Server DNS (reverse PTR)',
      clockSkew: 'Clock offset against the KDC',
      machineAccountRights: 'Join account rights on the OU',
      directoryBindRights: 'Bind account rights on the subtree',
      sharePrincipal: 'Share principal exists in the domain'
    },
    refound: 'Refound',
    refoundHint:
      'Rebind this realm name to a DIFFERENT domain (a legitimately rebuilt domain). Sudo plus three typed values read off the realm.',
    refoundBlockedNoSid: 'Nothing to refound: no domain SID is recorded yet — the first join records it.',
    refoundTitle: 'Refound realm {name}',
    refoundWarning:
      'Refoundation rebinds the NAME to a different domain SID — and does NOT release, renumber or reuse a single uid. The old numbers go on owning the old files; the new domain’s people get NEW numbers from the same range. Old files will show an owner nobody resolves any more: that is the true cost of a rebuilt domain.',
    refoundCurrent: 'Currently bound to {sid} — {count} allocated identities.',
    refoundConfirmLabel: 'Type the realm name ({name})',
    refoundSidLabel: 'New domain SID',
    refoundSidHint: 'S-1-5-21-… — the domain that owns this name FROM NOW ON. It must differ from the recorded one.',
    refoundSidSame: 'This is the SID already recorded — that is not a refoundation.',
    refoundAckLabel: 'Acknowledge the allocation count',
    refoundAckHint:
      'Type the realm’s CURRENT allocated identities, exactly — a number that moved under you means somebody allocated while you were deciding.',
    refoundDone: 'Rebound.',
    refoundSidChange: 'Domain SID: {previous} → {current}',
    refoundRetained: 'Retained allocations: {count} — every one of them.',
    revokeSessions: 'Revoke sessions',
    revokeTitle: 'Revoke the realm’s web sessions — {name}',
    revokeBody:
      'Throws every user of this realm out of the web explorer and DESTROYS their document-editor tokens: an open editor stops being able to save within seconds — unsaved work is lost. Kicking out is not disabling: the accounts keep existing IN THE DIRECTORY and can log in again (disable them there if they must stay out). Established SMB sessions are not cut.',
    revokeConfirm: 'Revoke now',
    revokeDone: 'Revoked: {sessions} web sessions, {tokens} editor tokens.'
  }
}

export const it: typeof en = {
  realms: {
    title: 'Realm di directory',
    createTitle: 'Dichiara un realm di directory',
    editTitle: 'Modifica realm {name}',
    edit: 'Modifica',
    createHint:
      'Dichiarare un realm è una DICHIARAZIONE: su nessun nodo cambia nulla — il join avviene quando una condivisione si lega al realm. Dopo, esegui la preflight: controlla i prerequisiti uno per uno, ciascuno col suo rimedio. Ogni mutazione qui è protetta da sudo.',
    editHint:
      'Modificabili: credenziale, server, i campi del tipo, la chiave d’identità (solo finché nessuno uid è inciso), la finestra offline e le note. Nome, tipo, intervallo uid e SID di dominio sono immutabili — l’intervallo è la chiave dei file già su disco; ricollegare il SID ha il suo gesto (rifondazione).',
    name: 'Nome',
    nameHint:
      'Etichetta DNS minuscola, unica su tutto il cluster, IMMUTABILE — è la metà qualificante di ogni login REALM\\utente e la chiave dell’intervallo uid. `local` e `files` sono riservati.',
    type: 'Tipo',
    types: {
      ad: 'Active Directory',
      ldap: 'LDAP'
    },
    typeHints: {
      ad: 'Kerberos/NTLMSSP contro un domain controller, winbind come resolver — l’unico ramo che SMB può usare. Immutabile.',
      ldap: 'Una directory LDAP semplice: bind su TLS obbligatorio, SSSD come resolver — serve SFTP/FTP e sessioni web, mai SMB. Immutabile.'
    },
    credential: 'Account di directory',
    credentialHint:
      'La voce del keystore di tipo directoryAccount con cui l’appliance si autentica: l’account di JOIN su AD, l’account di BIND su LDAP. Il segreto vive nel keystore — il realm porta il riferimento.',
    credentialRepointHint:
      'Ripuntare il riferimento è il modo per sostituire l’account di join/bind — mai un realm nuovo.',
    noCredentials:
      'Non esiste ancora una credenziale directoryAccount — creane una in Sistema → Credenziali prima: il realm porta il riferimento, il segreto vive nel keystore.',
    servers: 'Server di directory',
    serversHintAd:
      'Facoltativi su Active Directory: la lista VUOTA significa scoperta via record SRV DNS, che è la strada normale.',
    serversHintLdap:
      'Richiesti su LDAP (almeno uno): una directory semplice quasi mai pubblica record SRV, quindi una lista vuota è una configurazione che non può funzionare — rifiutata alla scrittura, non al primo login.',
    serverPlaceholder: 'host o host:porta',
    addServer: 'Aggiungi server',
    kerberosRealm: 'Realm Kerberos',
    kerberosRealmHint:
      'MAIUSCOLO (es. CORP.EXAMPLE) — i nomi di realm Kerberos distinguono le maiuscole; uno minuscolo fallisce al KDC con un messaggio che non nomina mai l’errore di battitura.',
    netbiosDomain: 'Dominio NetBIOS',
    netbiosDomainHint:
      'Il nome corto del dominio, MAIUSCOLO, max 15 caratteri — ciò che gli utenti Windows scrivono prima della barra rovescia.',
    machineAccountOu: 'OU del machine account',
    machineAccountOuHint:
      'Distinguished name della OU in cui nasce il machine account. Vuoto = il contenitore computer predefinito del dominio.',
    machineAccountOuDefault: 'Contenitore predefinito del dominio',
    baseDn: 'Base DN',
    baseDnHint: 'Il sottoalbero in cui l’appliance cerca (es. dc=example,dc=com). Non c’è un default.',
    tls: 'TLS',
    tlsHint:
      'Obbligatorio su questo ramo — verso la directory viaggia la password dell’utente, quindi il chiaro non si può nemmeno scrivere.',
    tlsOptions: {
      ldaps: 'LDAPS — TLS dal primo byte (636)',
      startTls: 'StartTLS — negoziato sulla porta in chiaro (389)'
    },
    userFilter: 'Filtro utenti',
    userFilterHint:
      'Filtro LDAP facoltativo che restringe quali voci contano come utenti. Vuoto = il default del resolver.',
    groupFilter: 'Filtro gruppi',
    groupFilterHint: 'Lo stesso, per i gruppi. Vuoto = il default del resolver.',

    // ENEA-A-R6 (2026-10-23): la metà Kerberos del realm ldap e la mappa gruppo→ruolo.
    kerberos: {
      title: 'Kerberos (MIT)',
      absent: 'Nessuno — SSSD autentica col bind LDAP',
      enable: 'Kerberos MIT: autentica contro un KDC e servi NFS krb5',
      enableHint:
        'Con questa, SSSD autentica gli utenti della directory contro i KDC qui sotto invece che col bind LDAP, e le share NFS del realm possono chiedere sec=krb5/krb5i/krb5p una volta agganciata la keytab nfs/. Il login web resta il bind LDAP.',
      realm: 'Realm Kerberos',
      realmHint: 'MAIUSCOLO (es. TVSTOR165.TEST) — per MIT le maiuscole contano.',
      kdcs: 'KDC',
      kdcsHint: 'Da uno a otto, host o host:porta, separati da virgole — provati in ordine; nessuna scoperta DNS.',
      nfsHostname: 'Nome host NFS',
      nfsHostnameHint:
        'La parte host del principal nfs/: il nome CONDIVISO con cui i client montano i gateway (un nome, N gateway), minuscolo.',
      adminServer: 'Admin server',
      adminServerHint: 'Facoltativo, informativo (l’appliance non esegue mai kadmin).',
      keytabCredential: 'Credenziale keytab nfs/',
      keytabCredentialHint:
        'Una credenziale machineAccount della forma KEYTAB con nfs/<host>@<REALM>: creata sul KDC con kadmin (addprinc -randkey, ktadd) e caricata con la CLI (create-credential --kind machineAccount --keytab-file …). Nessuna = ancora niente NFS Kerberos.',
      keytabNone: 'ancora nessuna'
    },
    adminRoles: {
      title: 'Login amministrativo (gruppo → ruolo)',
      hint: 'Chi sta in un gruppo mappato entra in console con il ruolo (tvstor-admin, tvstor-operator, tvstor-viewer o un ruolo personalizzato) sullo scope (vuoto = lo scope del realm). Chi non sta in nessun gruppo mappato resta utente kiosk. Il gruppo è un DN su ldap, @DOM\\nome su ad. Valutato a ogni richiesta dalla membership della sessione, rinfrescata ogni 15 minuti.',
      group: 'Gruppo',
      role: 'Ruolo',
      scope: 'Scope (facoltativo)',
      add: 'Aggiungi una mappatura',
      none: 'Nessun login amministrativo da questo realm'
    },
    declareUidRange: 'Dichiara l’intervallo uid',
    uidRangeHint:
      'Spento = la piattaforma prende il primo slot libero della griglia (2 000 000 + k·1 000 000). Acceso = si incidono la tua base e la tua ampiezza. IMMUTABILE in entrambi i casi: spostare un intervallo inciso cambierebbe il proprietario di file già scritti. Una sovrapposizione è rifiutata nominando il realm occupante.',
    uidBase: 'UID base',
    uidBaseHint:
      'Pavimento 2 000 000 (sotto vivono gli account di sistema); soffitto 1 000 000 000 (sopra inizia la finestra dei resolver derivata dal SID).',
    uidSpan: 'Ampiezza',
    uidSpanHint: 'Quanti uid il realm può incidere, a partire dalla base.',
    domainSid: 'SID di dominio',
    domainSidHint:
      'Facoltativo, solo AD (S-1-5-21-…): dichiaralo se lo conosci e il controllo nome-contro-dominio avviene ORA invece che a un join che fallisce. Omesso = lo registra il primo join.',
    domainSidNone: 'Nessun dominio ha ancora rivendicato questo nome',
    identityKeyAttribute: 'Attributo chiave d’identità',
    identityKeyAttributeHint:
      'Quale attributo della directory è la chiave STABILE di un utente. Vuoto = il default per tipo (AD → objectGUID, LDAP → entryUUID). Gli attributi noti come instabili sono rifiutati con la ragione.',
    identityKeyLocked:
      'Bloccato: questo realm ha già inciso degli uid — cambiare chiave ricambierebbe le persone dietro quei numeri.',
    offlineWindowSeconds: 'Finestra offline (secondi)',
    offlineWindowHint:
      'Per quanto un utente già autenticato continua a lavorare mentre la directory non risponde. 0 = ogni richiesta riconvalida (directory giù, nessuna sessione files). Vuoto = il compromesso predefinito, un’ora.',
    offlineWindowZero: '0 — ogni richiesta riconvalida',
    uidRange: 'Intervallo UID',
    uidRangeDeclared: 'Dichiarato',
    uidRangeAuto: 'Automatico',
    allocatedIdentities: 'Identità allocate',
    srvDiscovery: 'Scoperta SRV DNS',
    deleteTitle: 'Elimina realm {name}',
    deleteBody:
      'La dichiarazione se ne va; l’intervallo uid e le sue allocazioni RESTANO come lapide (un’allocazione non si rilascia mai — i file di quegli uid sono ancora su disco; ricreare il nome li riprende). Le sessioni web vive del realm sono revocate nella stessa transazione. Un realm da cui dipende una condivisione rifiuta, nominando le condivisioni.',
    preflight: 'Preflight',
    preflightTitle: 'Preflight — {name}',
    preflightHint:
      'Ciò che si può controllare PRIMA di toccare il dominio, un prerequisito alla volta. A tre valori di proposito: “sconosciuto” non significa mai a posto e non significa mai assente — ogni fallimento e ogni sconosciuto porta il suo rimedio. Su AD due prerequisiti sono strutturalmente inosservabili prima del join (scarto d’orologio, diritti sulla OU), quindi “pronto” non è raggiungibile in questa fase.',
    preflightRunning: 'Interrogo il nodo che risponde…',
    preflightAgain: 'Esegui di nuovo',
    observedFrom:
      'Osservato da {node} — l’adesione al dominio è per nodo: un esito buono significa visibile da QUESTO nodo, non da tutti.',
    outcomes: {
      ready: 'Pronto',
      blocked: 'Bloccato',
      inconclusive: 'Non conclusivo'
    },
    verdicts: {
      pass: 'Superato',
      fail: 'Fallito',
      unknown: 'Sconosciuto'
    },
    checks: {
      declaration: 'Forma della dichiarazione',
      credential: 'Credenziale directoryAccount',
      idRange: 'Intervallo uid inciso',
      idRangeDisjoint: 'Intervallo uid disgiunto',
      domainIdentity: 'Identità del dominio (SID)',
      identityKey: 'Attributo chiave d’identità',
      identityKeyInDirectory: 'Chiave d’identità nella directory',
      dnsSrv: 'Record SRV DNS',
      serverDns: 'DNS dei server (diretto)',
      reverseDns: 'DNS dei server (PTR inverso)',
      clockSkew: 'Scarto d’orologio verso il KDC',
      machineAccountRights: 'Diritti dell’account di join sulla OU',
      directoryBindRights: 'Diritti dell’account di bind sul sottoalbero',
      sharePrincipal: 'Il principal della condivisione esiste nel dominio'
    },
    refound: 'Rifonda',
    refoundHint:
      'Ricollega questo nome di realm a un dominio DIVERSO (un dominio legittimamente ricostruito). Sudo più tre valori digitati letti dal realm.',
    refoundBlockedNoSid: 'Niente da rifondare: nessun SID di dominio è ancora registrato — lo registra il primo join.',
    refoundTitle: 'Rifonda il realm {name}',
    refoundWarning:
      'La rifondazione ricollega il NOME a un SID di dominio diverso — e NON rilascia, rinumera o riusa un solo uid. I numeri vecchi continuano a possedere i file vecchi; le persone del dominio nuovo ricevono numeri NUOVI dallo stesso intervallo. I file vecchi mostreranno un proprietario che nessuno risolve più: è il costo vero di un dominio ricostruito.',
    refoundCurrent: 'Attualmente legato a {sid} — {count} identità allocate.',
    refoundConfirmLabel: 'Scrivi il nome del realm ({name})',
    refoundSidLabel: 'Nuovo SID di dominio',
    refoundSidHint:
      'S-1-5-21-… — il dominio che possiede questo nome DA ORA IN POI. Deve differire da quello registrato.',
    refoundSidSame: 'È il SID già registrato — questa non è una rifondazione.',
    refoundAckLabel: 'Riconosci il conteggio delle allocazioni',
    refoundAckHint:
      'Scrivi le identità allocate ATTUALI del realm, esatte — un numero che si è mosso sotto di te significa che qualcuno ha allocato mentre decidevi.',
    refoundDone: 'Ricollegato.',
    refoundSidChange: 'SID di dominio: {previous} → {current}',
    refoundRetained: 'Allocazioni mantenute: {count} — tutte quante.',
    revokeSessions: 'Revoca sessioni',
    revokeTitle: 'Revoca le sessioni web del realm — {name}',
    revokeBody:
      'Butta fuori dall’esploratore web ogni utente di questo realm e DISTRUGGE i token dell’editor di documenti: un editor aperto smette di poter salvare in pochi secondi — il lavoro non salvato è perso. Cacciare non è disabilitare: gli account continuano a esistere NELLA DIRECTORY e possono rientrare (disabilitali lì se devono restare fuori). Le sessioni SMB stabilite non vengono tagliate.',
    revokeConfirm: 'Revoca ora',
    revokeDone: 'Revocate: {sessions} sessioni web, {tokens} token editor.'
  }
}
